import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Tooltip,
  CircularProgress,
  Pagination,
  ButtonGroup,
  Alert,
  Snackbar,
  Stack,
  alpha
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BusinessIcon from '@mui/icons-material/Business';
import EditIcon from '@mui/icons-material/Edit';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ViewContractEmployees = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(7);
  const [totalPages, setTotalPages] = useState(0);
  const pdfRef = useRef(null);

  useEffect(() => {
    fetchContractEmployees();
  }, []);

  useEffect(() => {
    setTotalPages(Math.ceil(filteredEmployees.length / rowsPerPage));
    setPage(1);
  }, [filteredEmployees, rowsPerPage]);

  const fetchContractEmployees = async () => {
    try {
      const token = localStorage.getItem("token"); // get JWT token
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      setLoading(true);
      setError(null);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);

      const response = await axios.get(`${API_URL}/api/contract/employees/all`, {
        signal: controller.signal,
        timeout: 10000,
        headers: {
          
          Authorization: `Bearer ${token}`
        }
      });

      clearTimeout(timeoutId);

      if (response.data.success) {
        const employeesData = response.data.data || [];
        setEmployees(employeesData);
        setFilteredEmployees(employeesData);
        setSuccessMessage(`Successfully loaded ${employeesData.length} contract employees`);
      } else {
        throw new Error(response.data.message || 'Failed to load contract employees');
      }
    } catch (error) {
      //console.error('Error fetching contract employees:', error);
      
      if (error.name === 'AbortError') {
        setError('Request timed out. Please check your connection and try again.');
      } else if (error.code === 'ECONNABORTED') {
        setError('Connection timeout. Please check if the server is running.');
      } else if (error.response) {
        setError(`Server error: ${error.response.data?.message || error.response.statusText}`);
      } else if (error.request) {
        setError('Unable to connect to server. Please check if the backend service is running on port 5000.');
      } else {
        setError(`Error: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const retryFetch = () => {
    setError(null);
    fetchContractEmployees();
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);

    if (!term.trim()) {
      setFilteredEmployees(employees);
      return;
    }

    const filtered = employees.filter(employee => {
      const searchFields = [
        employee.c_employee_id,
        employee.fullName,
        employee.department,
        employee.companyName,
        employee.roleType,
        employee.designation,
        employee.email,
        employee.phoneNumber
      ];

      return searchFields.some(field => 
        field && field.toString().toLowerCase().includes(term)
      );
    });

    setFilteredEmployees(filtered);
  };

  const handleEdit = (employeeId) => {
    navigate('/contract/edit', {
      state: {
        employee_id: employeeId,
        isEdit: true
      }
    });
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const getCurrentPageData = () => {
    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredEmployees.slice(startIndex, endIndex);
  };

  const getDaysRemaining = (endDateStr) => {
    if (!endDateStr) return null;
    
    const endDate = new Date(endDateStr);
    const today = new Date();
    
    // Reset time portion for accurate day calculation
    endDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    
    const diffTime = endDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  const getContractStatusChip = (endDateStr, forPdf = false) => {
    if (!endDateStr) {
      return forPdf ? "No End Date" : <Chip label="No End Date" color="default" size="small" />;
    }
    
    const daysRemaining = getDaysRemaining(endDateStr);
    
    if (daysRemaining < 0) {
      return forPdf ? "Expired" : <Chip label="Expired" color="error" size="small" />;
    } else if (daysRemaining <= 30) {
      return forPdf ? `${daysRemaining} days left` : 
        <Chip label={`${daysRemaining} days left`} color="warning" size="small" />;
    } else {
      return forPdf ? `${daysRemaining} days left` : 
        <Chip label={`${daysRemaining} days left`} color="success" size="small" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  const createPrintableContent = () => {
    const printContent = document.createElement('div');
    printContent.style.padding = '20px';
    printContent.style.backgroundColor = 'white';
    printContent.style.fontFamily = 'Arial, sans-serif';
    
    const titleDiv = document.createElement('div');
    titleDiv.style.textAlign = 'center';
    titleDiv.style.marginBottom = '20px';
    titleDiv.innerHTML = `
      <h2 style="margin-bottom: 5px; color: #333;">Contract Employees Report</h2>
      <p style="margin-top: 5px; color: #666;">Generated on ${new Date().toLocaleDateString()}</p>
      <p style="color: #666;">Total Employees: ${filteredEmployees.length}</p>
    `;
    printContent.appendChild(titleDiv);
    
    const table = document.createElement('table');
    table.style.width = '100%';
    table.style.borderCollapse = 'collapse';
    table.style.marginTop = '20px';
    table.style.fontSize = '12px';
    
    const thead = document.createElement('thead');
    thead.innerHTML = `
      <tr style="background-color: #f5f5f5;">
        <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Employee ID</th>
        <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Name</th>
        <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Role</th>
        <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Department</th>
        <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Company</th>
        <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Projects</th>
        <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Start Date</th>
        <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">End Date</th>
        <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Status</th>
      </tr>
    `;
    table.appendChild(thead);
    
    const tbody = document.createElement('tbody');
    filteredEmployees.forEach(employee => {
      const tr = document.createElement('tr');
      
      let statusStyle = '';
      const status = getContractStatusChip(employee.contractEndDate, true);
      if (status === 'Expired') {
        statusStyle = 'color: #d32f2f; font-weight: bold;';
      } else if (status.includes('days left') && parseInt(status) <= 30) {
        statusStyle = 'color: #ed6c02; font-weight: bold;';
      } else if (status.includes('days left')) {
        statusStyle = 'color: #2e7d32; font-weight: bold;';
      }
      
      const projectNames = employee.projects && employee.projects.length > 0 
        ? employee.projects.map(p => p.name).join(', ')
        : 'No projects assigned';
      
      tr.innerHTML = `
        <td style="border: 1px solid #ddd; padding: 8px;">${employee.c_employee_id || ''}</td>
        <td style="border: 1px solid #ddd; padding: 8px;">${employee.fullName || ''}</td>
        <td style="border: 1px solid #ddd; padding: 8px;">${employee.roleType || ''}</td>
        <td style="border: 1px solid #ddd; padding: 8px;">${employee.department || ''}</td>
        <td style="border: 1px solid #ddd; padding: 8px;">${employee.companyName || ''}</td>
        <td style="border: 1px solid #ddd; padding: 8px;">${projectNames}</td>
        <td style="border: 1px solid #ddd; padding: 8px;">${formatDate(employee.contractStartDate)}</td>
        <td style="border: 1px solid #ddd; padding: 8px;">${formatDate(employee.contractEndDate)}</td>
        <td style="border: 1px solid #ddd; padding: 8px; ${statusStyle}">${status}</td>
      `;
      tbody.appendChild(tr);
    });
    table.appendChild(tbody);
    
    printContent.appendChild(table);
    
    return printContent;
  };

  const handlePrint = () => {
    try {
      const printContent = createPrintableContent();
      
      const printFrame = document.createElement('iframe');
      printFrame.style.position = 'absolute';
      printFrame.style.top = '-1000px';
      printFrame.style.left = '-1000px';
      document.body.appendChild(printFrame);
      
      printFrame.contentDocument.open();
      printFrame.contentDocument.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Contract Employees Report</title>
            <style>
              @media print {
                body { margin: 0; padding: 15px; }
                table { page-break-inside: auto; }
                tr { page-break-inside: avoid; page-break-after: auto; }
                thead { display: table-header-group; }
                @page { size: landscape; margin: 0.5in; }
              }
            </style>
          </head>
          <body>
            ${printContent.outerHTML}
          </body>
        </html>
      `);
      printFrame.contentDocument.close();
      
      printFrame.onload = () => {
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();
        
        setTimeout(() => {
          document.body.removeChild(printFrame);
        }, 1000);
      };
    } catch (error) {
      //console.error('Print error:', error);
      setError('Error printing. Please try again.');
    }
  };

  // const generatePDF = async () => {
  //   try {
  //     setLoading(true);
  //     const pdfContent = createPrintableContent();
      
  //     document.body.appendChild(pdfContent);
      
  //     const canvas = await html2canvas(pdfContent, { 
  //       scale: 1.2,
  //       useCORS: true,
  //       logging: false,
  //       width: pdfContent.scrollWidth,
  //       height: pdfContent.scrollHeight
  //     });
      
  //     document.body.removeChild(pdfContent);
      
  //     const imgData = canvas.toDataURL('image/png');
  //     const pdf = new jsPDF('l', 'mm', 'a4');
      
  //     const pdfWidth = pdf.internal.pageSize.getWidth();
  //     const pdfHeight = pdf.internal.pageSize.getHeight();
  //     const imgWidth = canvas.width;
  //     const imgHeight = canvas.height;
  //     const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
  //     const imgX = (pdfWidth - imgWidth * ratio) / 2;
  //     const imgY = 10;
      
  //     pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
  //     pdf.save(`Contract_Employees_Report_${new Date().toISOString().split('T')[0]}.pdf`);
      
  //     setSuccessMessage('PDF generated successfully!');
  //   } catch (error) {
  //     //console.error('PDF generation error:', error);
  //     setError('Error generating PDF. Please try again.');
  //   } finally {
  //     setLoading(false);
  //   }
  // };



  const generatePDF = async () => {
  try {
    setLoading(true);
    
    // Create a clean container for PDF content
    const pdfContainer = document.createElement('div');
    pdfContainer.style.position = 'fixed';
    pdfContainer.style.top = '-9999px';
    pdfContainer.style.left = '-9999px';
    pdfContainer.style.width = '1200px';
    pdfContainer.style.padding = '20px';
    pdfContainer.style.backgroundColor = 'white';
    pdfContainer.style.fontFamily = 'Arial, sans-serif';
    pdfContainer.style.zIndex = '-9999';
    
    // Create title
    const titleDiv = document.createElement('div');
    titleDiv.style.textAlign = 'center';
    titleDiv.style.marginBottom = '20px';
    titleDiv.innerHTML = `
      <h2 style="margin-bottom: 5px; color: #333;">Contract Employees Report</h2>
      <p style="margin-top: 5px; color: #666;">Generated on ${new Date().toLocaleDateString()}</p>
      <p style="color: #666;">Total Employees: ${filteredEmployees.length}</p>
    `;
    pdfContainer.appendChild(titleDiv);
    
    // Create table
    const table = document.createElement('table');
    table.style.width = '100%';
    table.style.borderCollapse = 'collapse';
    table.style.marginTop = '20px';
    table.style.fontSize = '12px';
    
    // Table header
    const thead = document.createElement('thead');
    thead.innerHTML = `
      <tr style="background-color: #f5f5f5;">
        <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-weight: bold;">Employee ID</th>
        <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-weight: bold;">Name</th>
        <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-weight: bold;">Role</th>
        <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-weight: bold;">Department</th>
        <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-weight: bold;">Projects</th>
        <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-weight: bold;">Company</th>
        <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-weight: bold;">Start Date</th>
        <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-weight: bold;">End Date</th>
        <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-weight: bold;">Status</th>
      </tr>
    `;
    table.appendChild(thead);
    
    // Table body
    const tbody = document.createElement('tbody');
    filteredEmployees.forEach(employee => {
      const tr = document.createElement('tr');
      
      let statusText = '';
      let statusStyle = '';
      const daysRemaining = getDaysRemaining(employee.contractEndDate);
      
      if (!employee.contractEndDate) {
        statusText = 'No End Date';
      } else if (daysRemaining < 0) {
        statusText = 'Expired';
        statusStyle = 'color: #d32f2f; font-weight: bold;';
      } else if (daysRemaining <= 30) {
        statusText = `${daysRemaining} days left`;
        statusStyle = 'color: #ed6c02; font-weight: bold;';
      } else {
        statusText = `${daysRemaining} days left`;
        statusStyle = 'color: #2e7d32; font-weight: bold;';
      }
      
      const projectNames = employee.projects && employee.projects.length > 0 
        ? employee.projects.map(p => p.name).join(', ')
        : 'No projects assigned';
      
      tr.innerHTML = `
        <td style="border: 1px solid #ddd; padding: 8px;">${employee.c_employee_id || ''}</td>
        <td style="border: 1px solid #ddd; padding: 8px;">
          <div style="font-weight: medium;">${employee.fullName || ''}</div>
          <div style="font-size: 10px; color: #666;">${employee.email || ''}</div>
        </td>
        <td style="border: 1px solid #ddd; padding: 8px;">${employee.roleType || ''}</td>
        <td style="border: 1px solid #ddd; padding: 8px;">${employee.department || ''}</td>
        <td style="border: 1px solid #ddd; padding: 8px; max-width: 150px;">${projectNames}</td>
        <td style="border: 1px solid #ddd; padding: 8px;">${employee.companyName || ''}</td>
        <td style="border: 1px solid #ddd; padding: 8px;">${formatDate(employee.contractStartDate)}</td>
        <td style="border: 1px solid #ddd; padding: 8px;">${formatDate(employee.contractEndDate)}</td>
        <td style="border: 1px solid #ddd; padding: 8px; ${statusStyle}">${statusText}</td>
      `;
      tbody.appendChild(tr);
    });
    table.appendChild(tbody);
    pdfContainer.appendChild(table);
    
    // Append to body temporarily
    document.body.appendChild(pdfContainer);
    
    // Capture the container
    const canvas = await html2canvas(pdfContainer, {
      scale: 2, // Higher quality
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      width: pdfContainer.scrollWidth,
      height: pdfContainer.scrollHeight,
      windowWidth: pdfContainer.scrollWidth,
      windowHeight: pdfContainer.scrollHeight
    });
    
    // Remove the temporary container
    document.body.removeChild(pdfContainer);
    
    // Generate PDF
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('l', 'mm', 'a4');
    
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
    const imgX = (pdfWidth - imgWidth * ratio) / 2;
    const imgY = 10;
    
    pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
    pdf.save(`Contract_Employees_Report_${new Date().toISOString().split('T')[0]}.pdf`);
    
    setSuccessMessage('PDF generated successfully!');
  } catch (error) {
    //console.error('PDF generation error:', error);
    setError('Error generating PDF. Please try again.');
  } finally {
    setLoading(false);
  }
};
  const handleCloseSnackbar = () => {
    setSuccessMessage('');
    setError('');
  };

  const handleViewNDA = (filename) => {
    if (!filename) return;
    const fileUrl = filename.startsWith('http') ? filename : `${API_URL}/uploads/${filename}`;
    window.open(fileUrl, '_blank');
  };

  return (
    <Box sx={{ maxWidth: "1500px", margin: 'auto', padding: 3, position: 'relative' }}>

      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" >
          View Contract Employees
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {loading
            ? 'Loading contract workforce records…'
            : `${filteredEmployees.length} of ${employees.length} employee${employees.length === 1 ? '' : 's'} shown`}
        </Typography>
      </Box>

      {/* Toolbar */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <TextField
          placeholder="Search by name, ID, company, department, or role"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1, maxWidth: 480 }}
        />

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            color="inherit"
            startIcon={<PictureAsPdfIcon fontSize="small" />}
            onClick={generatePDF}
            disabled={loading || filteredEmployees.length === 0}
            sx={{ borderRadius: 1.5 }}
          >
            Export PDF
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="inherit"
            startIcon={<PrintIcon fontSize="small" />}
            onClick={handlePrint}
            disabled={loading || filteredEmployees.length === 0}
            sx={{ borderRadius: 1.5 }}
          >
            Print
          </Button>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError('')}>
          <strong>Error:</strong> {error}
          <Button
            variant="outlined"
            size="small"
            onClick={retryFetch}
            sx={{ ml: 2 }}
            startIcon={<RefreshIcon />}
          >
            Retry
          </Button>
        </Alert>
      )}

      <div style={{ position: 'relative' }} ref={pdfRef}>
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            maxHeight: '70vh',
            overflow: 'auto',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.04em', textTransform: 'uppercase', color: 'text.secondary', bgcolor: 'grey.50', borderBottom: '2px solid', borderColor: 'divider' }}>Employee ID</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.04em', textTransform: 'uppercase', color: 'text.secondary', bgcolor: 'grey.50', borderBottom: '2px solid', borderColor: 'divider' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.04em', textTransform: 'uppercase', color: 'text.secondary', bgcolor: 'grey.50', borderBottom: '2px solid', borderColor: 'divider' }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.04em', textTransform: 'uppercase', color: 'text.secondary', bgcolor: 'grey.50', borderBottom: '2px solid', borderColor: 'divider' }}>Department</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.04em', textTransform: 'uppercase', color: 'text.secondary', bgcolor: 'grey.50', borderBottom: '2px solid', borderColor: 'divider' }}>Projects</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.04em', textTransform: 'uppercase', color: 'text.secondary', bgcolor: 'grey.50', borderBottom: '2px solid', borderColor: 'divider' }}>Company</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.04em', textTransform: 'uppercase', color: 'text.secondary', bgcolor: 'grey.50', borderBottom: '2px solid', borderColor: 'divider' }}>Start Date</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.04em', textTransform: 'uppercase', color: 'text.secondary', bgcolor: 'grey.50', borderBottom: '2px solid', borderColor: 'divider' }}>End Date</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.04em', textTransform: 'uppercase', color: 'text.secondary', bgcolor: 'grey.50', borderBottom: '2px solid', borderColor: 'divider' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.04em', textTransform: 'uppercase', color: 'text.secondary', bgcolor: 'grey.50', borderBottom: '2px solid', borderColor: 'divider' }}>NDA Doc</TableCell>
                {/* <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.04em', textTransform: 'uppercase', color: 'text.secondary', bgcolor: 'grey.50', borderBottom: '2px solid', borderColor: 'divider' }}>Actions</TableCell> */}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={32} thickness={4} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Loading contract employees…</Typography>
                  </TableCell>
                </TableRow>
              ) : getCurrentPageData().length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                    <Typography variant="body1" color="text.secondary" fontWeight={600}>
                      {searchTerm ? 'No employees found matching your search.' : 'No contract employees found.'}
                    </Typography>
                    {searchTerm && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setSearchTerm('');
                          setFilteredEmployees(employees);
                        }}
                        sx={{ mt: 2, borderRadius: 1.5 }}
                      >
                        Clear Search
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                getCurrentPageData().map((employee, index) => {
                  const projectNames = employee.projects && employee.projects.length > 0 
                    ? employee.projects.map(p => p.name).join(', ')
                    : 'No projects assigned';

                  return (
                    <TableRow key={employee.c_employee_id || index} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {employee.c_employee_id || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {employee.fullName || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {employee.email || 'No email'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {employee.roleType || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<BusinessIcon fontSize="small" />}
                          label={employee.department || 'N/A'}
                          variant="outlined"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={projectNames} arrow>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              maxWidth: 150, 
                              // overflow: 'hidden', 
                              // textOverflow: 'ellipsis',
                              whiteSpace: 'wrap'
                            }}
                          >
                            {projectNames}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {employee.companyName || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CalendarTodayIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {formatDate(employee.contractStartDate)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CalendarTodayIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {formatDate(employee.contractEndDate)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {getContractStatusChip(employee.contractEndDate)}
                      </TableCell>
                      <TableCell>
                        {employee.ndaDocPath ? (
                          <Button
                            variant="outlined"
                            size="small"
                            color="success"
                            onClick={() => handleViewNDA(employee.ndaDocPath)}
                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
                          >
                            View NDA
                          </Button>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            Not Uploaded
                          </Typography>
                        )}
                      </TableCell>
                      {/* <TableCell>
                        <ButtonGroup size="small" variant="outlined">
                          <Tooltip title="Edit Employee">
                            <Button
                              startIcon={<EditIcon />}
                              onClick={() => handleEdit(employee.c_employee_id)}
                              color="primary"
                            >
                              Edit
                            </Button>
                          </Tooltip>
                        </ButtonGroup>
                      </TableCell> */}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      {/* Pagination */}
      {!loading && filteredEmployees.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            shape="rounded"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Summary Statistics */}
      {/* {!loading && employees.length > 0 && (
        <Paper elevation={2} sx={{ mt: 3, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Summary Statistics
          </Typography>
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="body2" color="textSecondary">
                Total Contract Employees
              </Typography>
              <Typography variant="h6" color="primary">
                {employees.length}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="textSecondary">
                Filtered Results
              </Typography>
              <Typography variant="h6" color="secondary">
                {filteredEmployees.length}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="textSecondary">
                Contracts Expiring Soon (≤30 days)
              </Typography>
              <Typography variant="h6" color="warning.main">
                {employees.filter(emp => {
                  const days = getDaysRemaining(emp.contractEndDate);
                  return days !== null && days >= 0 && days <= 30;
                }).length}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="textSecondary">
                Expired Contracts
              </Typography>
              <Typography variant="h6" color="error.main">
                {employees.filter(emp => {
                  const days = getDaysRemaining(emp.contractEndDate);
                  return days !== null && days < 0;
                }).length}
              </Typography>
            </Box>
          </Box>
        </Paper>
      )} */}

      {/* Success/Error Snackbars */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ViewContractEmployees;